// 2026-10-08 — Commodities Journal (backend commodity_journal.py). Closed paper
// trades per underlying × strategy, with a "ready for live?" verdict.
// Rupees are per lot, recomputed with the real MCX units per lot.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, CheckCircle2, XCircle } from "lucide-react";
import axios, { API, fmt, pctColor, STRATEGY_LABELS } from "./commoditiesLib";

const PERIODS = [14, 30, 45];
const rs = (v) => (v == null ? "—" : `${v >= 0 ? "" : "−"}₹${fmt(Math.abs(v), 0)}`);

const Stats = ({ s }) => (
  <>
    <td className="px-3 py-1.5 text-right">{s.trades ?? 0}</td>
    <td className="px-3 py-1.5 text-right">{s.win_rate_pct == null ? "—" : `${fmt(s.win_rate_pct, 1)}%`}</td>
    <td className="px-3 py-1.5 text-right" style={{ color: s.profit_factor == null ? undefined : s.profit_factor >= 1 ? "var(--bull)" : "var(--bear)" }}>
      {s.profit_factor == null ? "—" : fmt(s.profit_factor, 2)}
    </td>
    <td className="px-3 py-1.5 text-right font-semibold" style={{ color: pctColor(s.net_rs) }}>{rs(s.net_rs)}</td>
    <td className="px-3 py-1.5 text-right" style={{ color: pctColor(s.avg_net_rs) }}>{rs(s.avg_net_rs)}</td>
    <td className="px-3 py-1.5 text-right text-muted-2">{rs(s.avg_win_rs)} / {rs(s.avg_loss_rs)}</td>
    <td className="px-3 py-1.5 text-right" style={{ color: "var(--bear)" }}>{rs(s.max_dd_rs)}</td>
  </>
);

const HEAD = ["Trades", "Win rate", "Profit factor", "Net ₹ / lot", "Avg / trade", "Avg win / loss", "Max DD"];

const Table = ({ title, rows, label, extraHead, extra }) => (
  <div className="border border-grid">
    <div className="px-3 py-2 border-b border-grid text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">{title}</div>
    {!rows?.length ? (
      <div className="px-3 py-3 text-xs text-muted-3 font-mono-data">No closed trades in this period.</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] font-mono-data">
          <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
            <tr className="border-b border-grid">
              <th className="text-left px-3 py-1.5 font-normal"> </th>
              {HEAD.map((h) => <th key={h} className="text-right px-3 py-1.5 font-normal">{h}</th>)}
              {extraHead}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-grid/40 align-top">
                <td className="px-3 py-1.5 text-muted-1 whitespace-nowrap">{label(r)}</td>
                <Stats s={r} />
                {extra && extra(r)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// 2026-10-09 — why paper and the real P&L page disagree: which signals were
// taken live (selection) and how much the real fills cost (slippage).
const cell = "px-3 py-1.5 text-right";
const head = "text-right px-3 py-1.5 font-normal";
const Rs = ({ v, bad }) => (
  <span style={{ color: v == null || v === 0 ? undefined : (bad ? v > 0 : v < 0) ? "var(--bear)" : "var(--bull)" }}>{rs(v)}</span>
);

const PaperVsLive = ({ pv }) => {
  const [open, setOpen] = useState(false);
  const rows = pv?.by_strategy || [];
  return (
    <div className="border border-grid">
      <div className="px-3 py-2 border-b border-grid text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
        Paper vs live (Kite) — ₹ per lot
      </div>
      {!rows.length ? (
        <div className="px-3 py-3 text-xs text-muted-3 font-mono-data">No live trades in this period.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono-data">
            <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
              <tr className="border-b border-grid">
                <th className="text-left px-3 py-1.5 font-normal"> </th>
                <th className={head}>All paper</th>
                <th className={head}>Taken live</th>
                <th className={head}>Same trades · paper</th>
                <th className={head}>Same trades · real</th>
                <th className={head}>Entry slippage</th>
                <th className={head}>Exit slippage</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.underlying}-${r.strategy}`} className="border-b border-grid/40">
                  <td className="px-3 py-1.5 text-muted-1 whitespace-nowrap">
                    <div className="font-semibold">{r.underlying}</div>
                    <div className="text-muted-3">{STRATEGY_LABELS[r.strategy] || r.strategy}</div>
                  </td>
                  <td className={cell}>{r.paper_all_n} · <Rs v={r.paper_all_net_rs} /></td>
                  <td className={cell}>{r.live_n}{r.live_unpaired_n ? <span className="text-muted-3"> (+{r.live_unpaired_n} no fill data)</span> : null}</td>
                  <td className={cell}><Rs v={r.live_paper_net_rs} /></td>
                  <td className={`${cell} font-semibold`}><Rs v={r.live_real_net_rs} /></td>
                  <td className={cell}><Rs v={r.entry_slip_rs} bad /></td>
                  <td className={cell}><Rs v={r.exit_slip_rs} bad /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-3 py-2 text-[10px] text-muted-3 font-mono-data">
            All paper vs Same trades · paper = effect of which signals were taken live.
            Same trades · paper vs real = execution cost (slippage = real fill worse than paper; positive is a cost).
            {pv.trades?.length ? (
              <button className="ml-2 underline" onClick={() => setOpen((o) => !o)}>{open ? "Hide" : "Show"} trades</button>
            ) : null}
          </div>
          {open && (
            <table className="w-full text-[11px] font-mono-data border-t border-grid">
              <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
                <tr className="border-b border-grid">
                  <th className="text-left px-3 py-1.5 font-normal">Entry (IST)</th>
                  <th className="text-left px-3 py-1.5 font-normal">Strategy · exit</th>
                  <th className={head}>Paper in → out</th>
                  <th className={head}>Real in → out</th>
                  <th className={head}>Paper ₹</th>
                  <th className={head}>Real ₹</th>
                  <th className={head}>Slip in / out</th>
                  <th className={head}>Order after</th>
                  <th className={head}>Quote signal → order</th>
                  <th className={head}>Moved before order</th>
                  <th className={head}>Fill vs quote</th>
                </tr>
              </thead>
              <tbody>
                {pv.trades.map((t, i) => (
                  <tr key={i} className="border-b border-grid/40">
                    <td className="px-3 py-1.5 whitespace-nowrap">{t.entry_time}</td>
                    <td className="px-3 py-1.5 text-muted-2">{t.underlying} · {STRATEGY_LABELS[t.strategy] || t.strategy} · {t.close_reason}</td>
                    <td className={cell}>{t.paper_entry} → {t.paper_exit}</td>
                    <td className={cell}>{t.live_entry} → {t.live_exit}</td>
                    <td className={cell}><Rs v={t.paper_net_rs} /></td>
                    <td className={cell}><Rs v={t.live_net_rs} /></td>
                    <td className={cell}><Rs v={t.entry_slip_rs} bad /> / <Rs v={t.exit_slip_rs} bad /></td>
                    <td className={`${cell} text-muted-2 whitespace-nowrap`}>
                      {t.wait_min == null ? "—" : `${t.wait_min} min`}{t.confirmed_by ? ` · ${t.confirmed_by}` : ""}
                      {t.order_type ? ` · ${t.order_type}${t.limit_sent ? ` ${t.limit_sent}` : ""}` : ""}
                    </td>
                    <td className={`${cell} whitespace-nowrap`}>{t.signal_quote ?? "—"} → {t.order_quote ?? "—"}</td>
                    <td className={cell}><Rs v={t.move_before_order_rs} bad /></td>
                    <td className={cell}><Rs v={t.fill_vs_quote_rs} bad /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const CommoditiesJournal = () => {
  const [days, setDays] = useState(45);
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/commodities/journal`, { params: { days } });
      setD(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load journal");
    }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const o = d?.overall || {};
  return (
    <section className="space-y-4" data-testid="commodities-journal-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">Commodities · MCX</div>
          <h2 className="font-heading text-2xl flex items-center gap-2"><BookOpen size={20} /> Journal</h2>
          <div className="text-[10px] text-muted-3 font-mono-data mt-1">
            Closed paper trades, last {d?.days ?? days} days. ₹ is per lot, net of estimated charges, recomputed with the
            real units per lot. Ready for live = ≥ {d?.rules?.min_trades ?? 30} trades, profit factor ≥ {d?.rules?.min_pf ?? 1.3},
            net positive, profitable in both halves of the period, ≥ {Math.round((d?.rules?.min_clean_share ?? 0.8) * 100)}% priced off real quotes.
          </div>
        </div>
        <div className="flex gap-2">
          {PERIODS.map((p) => (
            <button key={p} onClick={() => setDays(p)}
              className={`px-2 py-1 border text-[10px] font-mono-data ${days === p ? "text-muted-1 border-muted-2" : "text-muted-3 border-grid"}`}>{p}d</button>
          ))}
        </div>
      </div>

      {!d ? <div className="text-muted-2 font-mono-data text-xs">Loading…</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 font-mono-data">
            {[["Trades", o.trades ?? 0], ["Win rate", o.win_rate_pct == null ? "—" : `${fmt(o.win_rate_pct, 1)}%`],
              ["Profit factor", o.profit_factor == null ? "—" : fmt(o.profit_factor, 2)],
              ["Net ₹ / lot", rs(o.net_rs)], ["Max drawdown", rs(o.max_dd_rs)]].map(([k, v]) => (
              <div key={k} className="border border-grid p-3">
                <div className="text-[9px] uppercase tracking-[0.2em] text-muted-3">{k}</div>
                <div className="text-lg mt-1">{v}</div>
              </div>
            ))}
          </div>

          <Table
            title="Underlying × strategy — ready for live?"
            rows={d.by_combo}
            label={(r) => (
              <div>
                <div className="font-semibold">{r.underlying}</div>
                <div className="text-muted-3">{STRATEGY_LABELS[r.strategy] || r.strategy}</div>
              </div>
            )}
            extraHead={[
              <th key="h" className="text-right px-3 py-1.5 font-normal">Halves</th>,
              <th key="v" className="text-left px-3 py-1.5 font-normal">Live?</th>,
            ]}
            extra={(r) => [
              <td key="h" className="px-3 py-1.5 text-right text-muted-2 whitespace-nowrap">
                {r.verdict?.halves_net_rs ? r.verdict.halves_net_rs.map((x) => rs(x)).join(" · ") : "—"}
              </td>,
              <td key="v" className="px-3 py-1.5">
                {r.verdict?.ready ? (
                  <span className="flex items-center gap-1" style={{ color: "var(--bull)" }}><CheckCircle2 size={12} /> Ready</span>
                ) : (
                  <div>
                    <span className="flex items-center gap-1" style={{ color: "var(--bear)" }}><XCircle size={12} /> Not yet</span>
                    <div className="text-[10px] text-muted-3">{(r.verdict?.reasons || []).join(" · ")}</div>
                  </div>
                )}
              </td>,
            ]}
          />
          <PaperVsLive pv={d.paper_vs_live} />
          <Table title="By underlying" rows={d.by_underlying} label={(r) => r.key} />
          <Table title="By entry hour (IST)" rows={d.by_hour} label={(r) => (r.key == null ? "?" : `${String(r.key).padStart(2, "0")}:00`)} />
          <Table title="By exit reason" rows={d.by_exit} label={(r) => r.key || "—"} />
          <Table title="By day" rows={[...(d.by_day || [])].reverse()} label={(r) => r.key} />
          {d.excluded_rows > 0 && (
            <div className="text-[10px] text-muted-3 font-mono-data">{d.excluded_rows} closed rows had no entry/exit premium and are excluded.</div>
          )}
        </>
      )}
    </section>
  );
};

export default CommoditiesJournal;
